import { PRODUCTS } from '@/constants/products'
import { formatPriceInCents } from '@/lib/utils/format-currency'
import { clamp01, lerp, smoothstep01 } from '@/lib/utils/math'

type TagSide = 'left' | 'right'

interface TagAnchor {
  side: TagSide
  top: number
}

interface BouquetTag {
  slug: string
  name: string
  formattedPrice: string
  side: TagSide
  top: number
  appearAt: number
}

const SECTION_TITLE = 'Buquês assinados da casa'
const HEADING_TEXT = 'O buquê nasce diante dos seus olhos'
const KICKER_TEXT = 'Cada buquê é uma mensagem.'

const TAG_WINDOW = 0.52
const TAG_FADE = 0.08
const TAG_STAGGER = 0.082
const TAG_FIRST_APPEAR = 0.05
const RISE_FROM_PX = 26
const RISE_TO_PX = -22
const EDGE_DRIFT_PX = 20

const TAG_ANCHORS: TagAnchor[] = [
  { side: 'left', top: 15 },
  { side: 'right', top: 20 },
  { side: 'left', top: 37 },
  { side: 'right', top: 44 },
  { side: 'left', top: 61 },
  { side: 'right', top: 66 },
  { side: 'left', top: 84 },
  { side: 'right', top: 88 },
  { side: 'right', top: 32 },
]

const BOUQUET_TAGS: BouquetTag[] = PRODUCTS.filter(
  (product) => product.category === 'buques',
).flatMap((product, index) => {
  const anchor = TAG_ANCHORS[index]
  if (!anchor) {
    return []
  }
  return [
    {
      slug: product.slug,
      name: product.name,
      formattedPrice: formatPriceInCents(product.priceInCents),
      side: anchor.side,
      top: anchor.top,
      appearAt: TAG_FIRST_APPEAR + index * TAG_STAGGER,
    },
  ]
})

const CATALOG_ITEMS = BOUQUET_TAGS.map((tag) => ({
  slug: tag.slug,
  name: tag.name,
  formattedPrice: tag.formattedPrice,
}))

export interface BouquetSectionModelProps {
  progress: number
}

function resolveTagVisibility(
  progress: number,
  tag: BouquetTag,
): { opacity: number; offsetX: number; offsetY: number } {
  const disappearAt = tag.appearAt + TAG_WINDOW
  const fadeIn = smoothstep01((progress - tag.appearAt) / TAG_FADE)
  const fadeOut = smoothstep01((disappearAt - progress) / TAG_FADE)
  const opacity = clamp01(Math.min(fadeIn, fadeOut))
  const lifeT = clamp01((progress - tag.appearAt) / TAG_WINDOW)
  const edgeSign = tag.side === 'left' ? -1 : 1
  return {
    opacity,
    offsetX: lerp(EDGE_DRIFT_PX * edgeSign, 0, fadeIn),
    offsetY: lerp(RISE_FROM_PX, RISE_TO_PX, lifeT),
  }
}

export function useBouquetSectionModel({ progress }: BouquetSectionModelProps) {
  const globalExit = smoothstep01((1 - progress) / 0.08)

  const tags = BOUQUET_TAGS.map((tag) => {
    const visibility = resolveTagVisibility(progress, tag)
    const opacity = visibility.opacity * globalExit
    return {
      slug: tag.slug,
      name: tag.name,
      formattedPrice: tag.formattedPrice,
      side: tag.side,
      style: {
        top: `${tag.top}%`,
        opacity: Math.round(opacity * 1000) / 1000,
        transform: `translate3d(${visibility.offsetX.toFixed(2)}px, ${visibility.offsetY.toFixed(2)}px, 0)`,
        pointerEvents: opacity > 0.6 ? ('auto' as const) : ('none' as const),
      },
    }
  })

  const kickerFade = clamp01(
    Math.min(smoothstep01(progress / 0.1), smoothstep01((1 - progress) / 0.1)),
  )

  return {
    sectionTitle: SECTION_TITLE,
    headingText: HEADING_TEXT,
    kickerText: KICKER_TEXT,
    kickerStyle: { opacity: Math.round(kickerFade * 1000) / 1000 },
    tags,
    catalogItems: CATALOG_ITEMS,
    stageInert: progress <= 0 || progress >= 1,
  }
}
